import { formatMontantFCFA } from "@/lib/formatMontant";

const fmtMontant = (n: number) => formatMontantFCFA(Number(n) || 0);

export const TABLE_STYLES = {
  wrapper: {
    background: "white",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    overflow: "hidden" as const,
  },
  header: {
    padding: "12px 20px",
    background: "#fafafa",
    borderBottom: "1px solid #f1f5f9",
    fontSize: 11,
    fontWeight: 700,
    color: "#94a3b8",
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
  },
  headerCell: { padding: "12px 20px", textAlign: "left" as const },
  headerCellRight: { padding: "12px 20px", textAlign: "right" as const, minWidth: 100 },
  bodyCell: {
    padding: "14px 20px",
    fontFamily: "'JetBrains Mono', 'SF Mono', 'Consolas', monospace",
    fontWeight: 500,
    color: "#0f172a",
    fontSize: 14,
  },
  bodyCellLib: { padding: "14px 20px", color: "#475569", fontSize: 14, fontFamily: "'IBM Plex Sans', system-ui, sans-serif" },
  bodyCellAmount: {
    padding: "14px 20px",
    textAlign: "right" as const,
    fontVariantNumeric: "tabular-nums" as const,
    minWidth: 100,
    fontSize: 14,
    fontFamily: "'JetBrains Mono', 'SF Mono', 'Consolas', monospace",
  },
  totalLabelCell: { padding: "14px 20px", fontSize: 14, fontWeight: 600 },
  totalRow: {
    borderTop: "1px solid #e2e8f0",
    background: "#f8fafc",
    fontWeight: 600,
  },
};

/** Marges et corps réduits (drawers factures, etc.) */
export const TABLE_STYLES_COMPACT = {
  ...TABLE_STYLES,
  header: {
    ...TABLE_STYLES.header,
    padding: "8px 14px",
    fontSize: 10,
    letterSpacing: "0.05em",
  },
  headerCell: { padding: "8px 14px", textAlign: "left" as const },
  headerCellRight: { padding: "8px 14px", textAlign: "right" as const, minWidth: 88 },
  bodyCell: {
    ...TABLE_STYLES.bodyCell,
    padding: "6px 14px",
    fontSize: 12,
    lineHeight: 1.3,
  },
  bodyCellLib: {
    ...TABLE_STYLES.bodyCellLib,
    padding: "6px 14px",
    fontSize: 12,
    lineHeight: 1.35,
  },
  bodyCellAmount: {
    ...TABLE_STYLES.bodyCellAmount,
    padding: "6px 14px",
    fontSize: 12,
    minWidth: 88,
    lineHeight: 1.3,
  },
  totalLabelCell: { padding: "8px 14px", fontSize: 12, fontWeight: 600 },
};

const DEFAULT_PLACEHOLDER_ROWS = [
  { c: "6xx", l: "Charge" },
  { c: "445660", l: "TVA déductible" },
  { c: "4011", l: "Fournisseurs" },
];

export interface TableauComptableProps {
  lignes?: { compte_numero: string; compte_libelle: string; debit: number; credit: number; id: string }[];
  totalDebit?: number;
  totalCredit?: number;
  empty?: boolean;
  emptyMessage?: string;
  emptySubtext?: string;
  subheader?: string;
  embedded?: boolean;
  /** Réduit padding et tailles de police (barres latérales compta). */
  compact?: boolean;
  placeholderRows?: { c: string; l: string }[];
}

export default function TableauComptable({
  lignes,
  totalDebit,
  totalCredit,
  empty,
  emptyMessage,
  emptySubtext,
  subheader,
  embedded,
  compact = false,
  placeholderRows = DEFAULT_PLACEHOLDER_ROWS,
}: TableauComptableProps) {
  const styles = compact ? TABLE_STYLES_COMPACT : TABLE_STYLES;
  const embeddedPad = compact ? "0 0 12px 0" : "0 0 20px 0";
  return (
    <div style={embedded ? { padding: embeddedPad } : styles.wrapper}>
      {subheader && (
        <div
          style={{
            padding: "12px 20px",
            background: "#fafafa",
            borderBottom: "1px solid #f1f5f9",
            fontSize: 13,
            color: "#94a3b8",
          }}
        >
          {subheader}
        </div>
      )}
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <thead>
          <tr style={styles.header}>
            <th style={{ ...styles.headerCell, width: compact ? 80 : 90 }}>Compte</th>
            <th style={{ ...styles.headerCell, width: "auto" }}>Libellé</th>
            <th style={styles.headerCellRight}>Débit</th>
            <th style={styles.headerCellRight}>Crédit</th>
          </tr>
        </thead>
        <tbody>
          {empty ? (
            <>
              {placeholderRows.map((r, i) => (
                <tr key={i} style={{ borderTop: i > 0 ? "1px solid #f1f5f9" : undefined }}>
                  <td style={{ ...styles.bodyCell, color: "#cbd5e1" }}>{r.c}</td>
                  <td style={{ ...styles.bodyCellLib, color: "#cbd5e1" }}>{r.l}</td>
                  <td style={{ ...styles.bodyCellAmount, color: "#e2e8f0" }}>—</td>
                  <td style={{ ...styles.bodyCellAmount, color: "#e2e8f0" }}>—</td>
                </tr>
              ))}
              <tr style={styles.totalRow}>
                <td colSpan={2} style={{ ...styles.totalLabelCell, color: "#64748b" }}>
                  Total
                </td>
                <td style={{ ...styles.bodyCellAmount, color: "#cbd5e1" }}>—</td>
                <td style={{ ...styles.bodyCellAmount, color: "#cbd5e1" }}>—</td>
              </tr>
            </>
          ) : (
            <>
              {lignes!.map((l, i) => (
                <tr
                  key={l.id}
                  style={{
                    borderTop: i > 0 ? "1px solid #f1f5f9" : undefined,
                    transition: "background 0.15s ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#fafafa"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  <td style={styles.bodyCell}>{l.compte_numero}</td>
                  <td style={styles.bodyCellLib}>{l.compte_libelle}</td>
                  <td
                    style={{
                      ...styles.bodyCellAmount,
                      fontWeight: 500,
                      color: l.debit > 0 ? "#0f172a" : "#cbd5e1",
                    }}
                  >
                    {l.debit > 0 ? fmtMontant(l.debit) : "—"}
                  </td>
                  <td
                    style={{
                      ...styles.bodyCellAmount,
                      fontWeight: 500,
                      color: l.credit > 0 ? "#0f172a" : "#cbd5e1",
                    }}
                  >
                    {l.credit > 0 ? fmtMontant(l.credit) : "—"}
                  </td>
                </tr>
              ))}
              <tr style={styles.totalRow}>
                <td colSpan={2} style={{ ...styles.totalLabelCell, color: "#0f172a" }}>
                  Total
                </td>
                <td style={{ ...styles.bodyCellAmount, fontWeight: 600, color: "#0f172a" }}>{fmtMontant(totalDebit || 0)}</td>
                <td style={{ ...styles.bodyCellAmount, fontWeight: 600, color: "#0f172a" }}>{fmtMontant(totalCredit || 0)}</td>
              </tr>
            </>
          )}
        </tbody>
      </table>
      {empty && (emptyMessage || emptySubtext) && (
        <div
          style={{
            padding: "14px 16px",
            background: "#FAFAFA",
            borderTop: "1px solid #F1F5F9",
            fontSize: "0.8rem",
          }}
        >
          <div style={{ fontWeight: 600, color: "#64748b", marginBottom: 2 }}>{emptyMessage}</div>
          {emptySubtext && (
            <div style={{ fontSize: "0.75rem", color: "#94a3b8", lineHeight: 1.4 }}>{emptySubtext}</div>
          )}
        </div>
      )}
    </div>
  );
}
